const addressStateKey = 'addressState';
const directorySearchFiltersKey = 'directorySearchFilters';
const enrollmentFormValuesKey = 'enrollmentFormValues';
const zipCodeKey = 'zipcode';
const formularyYearKey = 'formularyYear';
const formularyCountyKey = 'formularyCounty';
const formularyPbpKey = 'formularyPbp';

/**
 * @returns true, if running inside a browser with usable local storage
 * @see https://michalzalecki.com/why-using-localStorage-directly-is-a-bad-idea/
 */
const isSupported = (): boolean => {
  try {
    const key = '__supported_check__';
    localStorage.setItem(key, key);
    localStorage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
};

// todo: add fallback to cookie
const getPersistence = (key: string): string => {
  if (isSupported()) {
    return localStorage.getItem(key);
  }
  return;
};

const getPersistenceExpirable = (key: string): any => {
  const localStorageValues = isSupported()
    ? JSON.parse(window.localStorage.getItem(key))
    : null;
  const storedTimestamp = localStorageValues
    ? localStorageValues.timestamp
    : null;
  const isExpired = storedTimestamp
    ? Date.now() > storedTimestamp + 7200000 // two hours
    : false;

  if (isExpired) {
    window.localStorage.removeItem(key);
    return null;
  }

  return localStorageValues;
};

const removePersistence = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    return;
  }
};

// setPersistence only supports string values; objects must be serialized by caller
const setPersistence = (key: string, value: string): boolean => {
  if (isSupported()) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }
  return false;
};

const setPersistenceExpirable = (key: string, value: string): boolean =>
  setPersistence(key, JSON.stringify({[key]: value, timestamp: Date.now()}));

export const getAddressState = () => getPersistence(addressStateKey);
export const setAddressState = (value: string) =>
  setPersistence(addressStateKey, value);

export const getDirectorySearchFilters = () => {
  const filters = getPersistence(directorySearchFiltersKey);
  return filters ? JSON.parse(filters) : null;
};
export const setDirectorySearchFilters = (
  y: string,
  s: string,
  p: string,
  c: string,
  z: string,
) => {
  setPersistence(
    directorySearchFiltersKey,
    JSON.stringify({
      addressCounty: c,
      addressState: s,
      addressZip: z,
      directoryYear: y,
      planType: p,
    }),
  );
};

export const getEnrollmentFormValues = () =>
  getPersistenceExpirable(enrollmentFormValuesKey);
export const setEnrollmentFormValues = (value: string) =>
  setPersistence(enrollmentFormValuesKey, value);
export const removeEnrollmentFormValues = () =>
  removePersistence(enrollmentFormValuesKey);

/**
 * Indicates whether the current session has opted into a preview mode
 * that will show the next year's plans by default. There is no corresponding
 * getter because opt-in is done directly in the browser.
 */
export const previewNextYear = () =>
  getPersistence('previewNextYear') === 'true';

/** Retrieve a ZIP code for use in search forms. The value will expire two hours after it is first set. */
export const getZipCode = () =>
  getPersistenceExpirable(zipCodeKey)?.zipcode ?? '';
export const setZipCode = (value: string) =>
  setPersistenceExpirable(zipCodeKey, value);

export const getFormularyYear = () =>
  getPersistenceExpirable(formularyYearKey)?.formularyYear ?? '';
export const setFormularyYear = (value: string) =>
  setPersistenceExpirable(formularyYearKey, value);

export const getFormularyCounty = () =>
  getPersistenceExpirable(formularyCountyKey)?.formularyCounty ?? '';
export const setFormularyCounty = (value: string) =>
  setPersistenceExpirable(formularyCountyKey, value);

export const getFormularyPbp = () =>
  getPersistenceExpirable(formularyPbpKey)?.formularyPbp ?? '';
export const setFormularyPbp = (value: string) =>
  setPersistenceExpirable(formularyPbpKey, value);
